import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  Output,
  ViewChild,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { CustomerService, CustomerListParams } from '../../services/customer.service';
import { CustomerDetailsModalComponent } from '../customer-details-modal/customer-details-modal.component';
import { formatDateWithTimezone } from '../../shared/utils/date.util';
import { CustomerListItem } from '../../shared/types';
import { CustomerTagView, customerTagsOf } from '../../shared/utils/customer-tags.util';
import { ActionButtonComponent } from '../../shared/components/action-button/action-button.component';
import {
  createDefaultCustomerDiscountPageFilters,
  CustomerDiscountPageFilters,
} from '../../customer/utils/customer-discount-filter.util';

export type Customer = CustomerListItem;

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
}

@Component({
  selector: 'app-customer-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSortModule,
    MatChipsModule,
    MatButtonModule,
    MatTooltipModule,
    PaginationBarComponent,
    MatMenuModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    CustomerDetailsModalComponent,
    ActionButtonComponent,
  ],
  templateUrl: './customer-table.component.html',
  styleUrls: ['./customer-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CustomerTableComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Loaded rows from the customer list API (parent does not pass this as an @Input). */
  customers: Customer[] = [];
  @Output() addCustomerClick = new EventEmitter<void>();
  @ViewChild(MatSort) set matSort(sort: MatSort | undefined) {
    if (!sort) {
      return;
    }
    this.sort = sort;
    this.bindTableSort();
  }
  @ViewChild('statusTrigger') statusTrigger?: MatMenuTrigger;
  @ViewChild('lastVisitTrigger') lastVisitTrigger?: MatMenuTrigger;

  private sort: MatSort | null = null;

  loading = false;
  // Array of skeleton placeholder rows (length controls number of shimmer rows)
  skeletonRows: { __skeleton: true; id: number }[] = Array.from({ length: 5 }, (_, i) => ({
    __skeleton: true,
    id: i,
  }));
  selectedCustomer: Customer | null = null;
  showCustomerModal = false;

  // Local copy for client-side sorting
  sortedCustomers: Customer[] = [];
  originalCustomers: Customer[] = [];

  // Material table properties
  dataSource = new MatTableDataSource<Customer>();
  displayedColumns: string[] = [
    'name',
    'tags',
    'vehicles',
    'license',
    'visits',
    'revenue',
    'totalDiscountRedeemed',
    'lastVisit',
    'status',
  ];

  // Filter state
  isStatusFilterOpen = false;
  selectedStatuses: string[] = [];
  isLastVisitFilterOpen = false;
  selectedLastVisitFilter = '';
  customStartDate: Date | null = null;
  customEndDate: Date | null = null;
  lastVisitDateRangeError: string | null = null;
  searchText = '';
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private searchRequestId = 0;
  private openCustomerRequestId = 0;

  /** Shared Filter modal (Customers with Discounts multi-select only). */
  appliedDiscountPageFilters: CustomerDiscountPageFilters =
    createDefaultCustomerDiscountPageFilters();
  /** Lightweight customer rows for the Customers with Discounts multi-select (redeemers only). */
  private readonly destroy$ = new Subject<void>();

  // Filter options
  statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  lastVisitOptions = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'This Week', value: 'this_week' },
    { label: 'Last 7 Days', value: 'last_7_days' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Custom', value: 'custom' },
  ];

  // Pagination properties
  pagination: PaginationInfo = {
    page: 1,
    limit: 5,
    total: 0,
  };

  // Rows per page options for dropdown
  rowsPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
  ];

  // Math reference for template
  Math = Math;

  // Helper method to check if search or filters are active
  /**
   * Checks whether there is an active search text or any filter selected in the customer table.
   * @example
   * hasActiveSearchOrFilters()
   * true
   * @returns {boolean} True if any search text or filter is active, otherwise false.
   **/
  get hasActiveSearchOrFilters(): boolean {
    return Boolean(
      this.searchText?.trim() ||
      this.selectedStatuses?.length > 0 ||
      this.selectedLastVisitFilter ||
      this.filters.search ||
      this.filters.customer_status ||
      this.filters.start_date ||
      this.filters.end_date ||
      this.filters.id
    );
  }

  // Get empty state configuration based on whether search/filters are active
  /**
   * Returns an empty state configuration object (image, title, description) based on whether there is an active search or filters.
   * @example
   * emptyStateConfig()
   * { image: 'assets/customer/Illustrations.svg', title: 'Oops! No results found', description: 'Please try again or modify your search or filters' }
   * @param {{void}} {{none}} - No arguments.
   * @returns {{Object}} Return configuration object with 'image', 'title', and 'description' string properties.
   **/
  get emptyStateConfig() {
    if (this.hasActiveSearchOrFilters) {
      return {
        image: 'assets/customer/Illustrations.svg',
        title: 'Oops! No results found',
        description: 'Please try again or modify your search or filters',
      };
    }
    return {
      image: 'assets/images/empty-state-illustration.svg',
      title: 'No Customers Found',
      description: 'There are no customer records available yet.',
    };
  }

  // Sorting state
  currentSortField = '';
  currentSortOrder: 'asc' | 'desc' | '' = '';

  // Current filters
  filters: CustomerListParams = {
    page: 1,
    limit: 5,
  };

  constructor(private customerService: CustomerService) {}

  ngOnInit() {
    this.loadCustomers();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  }

  ngAfterViewInit() {
    this.bindTableSort();
  }

  private bindTableSort(): void {
    if (!this.sort) {
      return;
    }
    this.dataSource.sort = this.sort;
    this.dataSource.sortingDataAccessor = (item, property) => {
      const value = this.getFieldValue(item, property);
      if (value instanceof Date) {
        return value.getTime();
      }
      if (typeof value === 'string') {
        return value.toLowerCase();
      }
      if (typeof value === 'number') {
        return value;
      }
      return value ?? '';
    };
  }

  /**
   * Check if current filters only contain default parameters (page and limit)
   * and no other filters are applied
   */
  private hasOnlyDefaultFilters(): boolean {
    const { page: _page, limit: _limit, ...otherFilters } = this.filters;

    // Check if any other filters have values (not undefined, null, or empty string)
    return Object.values(otherFilters).every(
      value => value === undefined || value === null || value === ''
    );
  }

  /**
   * Loads customers from the API and updates component state (customers, pagination, loading, skeletonRows).
   * @example
   * oadCustomers({ page: 1, limit: 10 })
   * undefined
   * @param {{CustomerListParams}} {{params}} - Optional filters and pagination parameters to merge with current filters before fetching.
   * @returns {{void}} No direct return value; updates component state asynchronously.
   **/
  loadCustomers(params?: CustomerListParams) {
    this.loading = true;

    // Merge with current filters if params provided
    if (params) {
      this.filters = { ...this.filters, ...params };
    }
    // Prepare skeleton rows count based on the target page size
    {
      const count = this.filters.limit || this.pagination.limit || 5;
      this.skeletonRows = Array.from({ length: count }, (_, i) => ({ __skeleton: true, id: i }));
    }

    const requestId = ++this.searchRequestId;
    this.customerService.getCustomers(this.filters).subscribe({
      next: response => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        if (response.status && response.data) {
          const rawRows = this.extractCustomerListRows(response.data);
          this.customers = rawRows.map((customer: any) => this.transformCustomer(customer));
          this.dataSource.data = this.customers;
          this.bindTableSort();

          // Only store original customers if we have default params (page and limit) and no other filters
          const hasOnlyDefaultParams = this.hasOnlyDefaultFilters();
          if (hasOnlyDefaultParams) {
            this.originalCustomers = [...this.customers];
          }

          const pageInfo = this.extractPagination(response.data);
          if (pageInfo) {
            const page = pageInfo.page ?? this.filters.page ?? this.pagination.page ?? 1;
            const limit = pageInfo.limit ?? this.filters.limit ?? this.pagination.limit ?? 5;
            let total = pageInfo.total ?? this.pagination.total ?? 0;
            if (this.customers.length > 0) {
              const floor = (page - 1) * limit + this.customers.length;
              total = Math.max(total, floor);
            }
            this.pagination = { page, limit, total };
            this.filters.page = page;
            this.filters.limit = limit;
          } else if (this.customers.length > 0) {
            const page = this.filters.page ?? 1;
            const limit = this.filters.limit ?? this.pagination.limit ?? 5;
            const inferredTotal = Math.max(
              this.pagination.total,
              (page - 1) * limit + this.customers.length
            );
            this.pagination = { page, limit, total: inferredTotal };
          }
        } else {
          this.customers = [];
          this.dataSource.data = [];
          this.pagination = { ...this.pagination, total: 0 };
        }
        this.loading = false;
        // Adjust skeleton rows to match current page size
        {
          const count = this.pagination.limit || 5;
          this.skeletonRows = Array.from({ length: count }, (_, i) => ({
            __skeleton: true,
            id: i,
          }));
        }
      },
      error: error => {
        if (requestId !== this.searchRequestId) {
          return;
        }
        // API returns 404 when no customers match search/filters — treat as empty list
        const statusCode = error?.status ?? error?.error?.status_code;
        if (statusCode === 404) {
          this.customers = [];
          this.dataSource.data = [];
          this.pagination = { ...this.pagination, total: 0 };
        } else {
          console.error('Error loading customers:', error);
        }
        this.loading = false;
        {
          const count = this.pagination.limit || 5;
          this.skeletonRows = Array.from({ length: count }, (_, i) => ({
            __skeleton: true,
            id: i,
          }));
        }
      },
    });
  }

  /**
   * Transform a raw customer record into a Customer object with computed display fields.
   * @example
   * transformCustomer({ first_name: 'John', last_name: 'Doe', vehicle: [{make: 'Toyota'}], license_plate: 'ABC123', visit_count: 5, revenue: 250, last_visit_date: '2026-03-10' })
   * { first_name: 'John', last_name: 'Doe', name: 'John Doe', initials: 'JD', vehicles: [{make: 'Toyota'}], licensePlates: 'ABC123', numberOfVisits: 5, revenue: 250, lastVisit: '2026-03-10' }
   * @param {{any}} {{customer}} - Raw customer object from the data source.
   * @returns {{Customer}} Return transformed Customer object with computed fields for display.
   **/
  private transformCustomer(customer: any): Customer {
    const fullName = this.getFullName(customer.first_name, customer.last_name);
    const resolvedType = customer.type ?? customer.customer_type ?? customer.customerType ?? '';
    const visitCount =
      customer.visit_count ??
      customer.visitCount ??
      customer.number_of_visits ??
      customer.total_visits ??
      0;
    const rawRevenue = customer.revenue ?? customer.total_revenue ?? customer.lifetime_revenue;
    const revenueDisplay =
      rawRevenue === undefined || rawRevenue === null || rawRevenue === ''
        ? '—'
        : typeof rawRevenue === 'number'
          ? `$${rawRevenue.toFixed(2)}`
          : String(rawRevenue);
    const rawTotalDiscount =
      customer.total_discount_redeemed ??
      customer.totalDiscountRedeemed ??
      customer.total_discount ??
      0;
    const totalDiscountDisplay = this.formatMoneyDisplay(rawTotalDiscount);
    const lastVisitRaw =
      customer.last_visit_date ?? customer.last_visit ?? customer.lastVisit ?? customer.updated_at;
    return {
      ...customer,
      type: resolvedType,
      customer_status: customer.customer_status ?? customer.status ?? '',
      name: fullName, // Computed full name for display
      initials: this.getInitials(customer.first_name, customer.last_name),
      vehicles: this.formatVehicles(customer.vehicle ?? customer.vehicles),
      licensePlates: customer.license_plate ?? customer.licensePlate ?? customer.plate ?? 'N/A',
      numberOfVisits: Number(visitCount) || 0,
      revenue: revenueDisplay,
      totalDiscountRedeemed: totalDiscountDisplay,
      lastVisit: lastVisitRaw ? this.formatDate(lastVisitRaw) : '—',
    };
  }

  tagsOf(customer: Customer): CustomerTagView[] {
    return customerTagsOf(customer);
  }

  private formatMoneyDisplay(raw: unknown): string {
    if (raw === undefined || raw === null || raw === '') {
      return '$0.00';
    }
    if (typeof raw === 'number') {
      return `$${raw.toFixed(2)}`;
    }
    const asString = String(raw).trim();
    if (!asString) {
      return '$0.00';
    }
    if (asString.startsWith('$')) {
      const numeric = Number.parseFloat(asString.slice(1).replace(/,/g, ''));
      return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : asString;
    }
    const numeric = Number.parseFloat(asString.replace(/,/g, ''));
    return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : asString;
  }

  /**
   * Normalizes list payloads from GET /customer (and search) across common API shapes.
   */
  private extractCustomerListRows(data: any): any[] {
    if (!data || typeof data !== 'object') {
      return [];
    }
    const candidates = [
      data.customer,
      data.customers,
      data.items,
      data.results,
      data.list,
      Array.isArray(data.data) ? data.data : null,
    ];
    for (const c of candidates) {
      if (Array.isArray(c)) {
        return c;
      }
      if (c && typeof c === 'object') {
        return [c];
      }
    }
    return [];
  }

  private extractPagination(data: any): Partial<PaginationInfo> | null {
    if (!data || typeof data !== 'object') {
      return null;
    }
    const nested = data.pagination ?? data.meta ?? data.page_info ?? null;
    const p =
      nested && typeof nested === 'object'
        ? nested
        : {
            page: data.page,
            limit: data.limit,
            total: data.total ?? data.total_count ?? data.total_records,
          };
    if (!p || typeof p !== 'object') {
      return null;
    }
    const page = Number(p.page ?? p.current_page ?? p.page_number ?? p.currentPage);
    const limit = Number(p.limit ?? p.per_page ?? p.page_size ?? p.pageSize);
    const total = Number(p.total ?? p.total_count ?? p.total_records ?? p.count ?? p.totalItems);
    const out: Partial<PaginationInfo> = {};
    if (Number.isFinite(page) && page > 0) {
      out.page = page;
    }
    if (Number.isFinite(limit) && limit > 0) {
      out.limit = limit;
    }
    if (Number.isFinite(total) && total >= 0) {
      out.total = total;
    }
    return Object.keys(out).length ? out : null;
  }

  private getFullName(firstName: string, lastName: string | null): string {
    if (!firstName) return '';
    if (!lastName) return firstName;
    return `${firstName} ${lastName}`;
  }

  /**
   * Returns uppercase initials derived from the provided first and last name.
   * @example
   * getInitials('John', 'Doe')
   * 'JD'
   * @param {{string}} {{firstName}} - The user's first name; used to derive initials.
   * @param {{string|null}} {{lastName}} - The user's last name or null; if provided, its first character is used as the second initial.
   * @returns {{string}} Uppercase initials (two letters) derived from the names, or an empty string if no name is available.
   **/
  private getInitials(firstName: string, lastName: string | null): string {
    let initials = '';

    if (firstName) {
      initials += firstName.charAt(0).toUpperCase();
    }

    if (lastName) {
      initials += lastName.charAt(0).toUpperCase();
    }

    // If we only have firstName and no lastName, take first two characters of firstName
    if (!lastName && firstName.length > 1) {
      initials = firstName.substring(0, 2).toUpperCase();
    }

    return initials || '';
  }

  private formatVehicles(vehicle: any): string {
    if (!vehicle) return 'No vehicle';
    if (Array.isArray(vehicle)) {
      if (vehicle.length === 0) return 'No vehicle';
      const parts = vehicle
        .map(v => v?.make || v?.model || v?.license_plate || v?.licensePlate)
        .filter(Boolean);
      return parts.length ? parts.join(', ') : 'Vehicle';
    }
    return vehicle.make || vehicle.model || 'Vehicle';
  }

  private formatDate(dateString: string): string {
    return formatDateWithTimezone(dateString);
  }

  getStatusClass(status: string | undefined | null): string {
    return (status || '').toLowerCase() === 'active' ? 'active' : 'inactive';
  }

  refreshTable() {
    this.loadCustomers();
  }

  onCustomerNameClick(customer: Customer) {
    this.selectedCustomer = customer;
    this.showCustomerModal = true;
  }

  onCloseModal() {
    this.showCustomerModal = false;
    this.selectedCustomer = null;
  }

  /** Sequence of "open customer" requests; a stale answer is ignored. */
  private openCustomerRequestSeq = 0;

  /**
   * CWS-1881: a prepaid redemption row in the open drawer asked for another customer. The
   * drawer shows whatever it is handed, so fetch that customer and hand it over in place —
   * the attendant lands on the redeemer's record without going back through the table.
   */
  onOpenCustomerById(customerId: string): void {
    const id = String(customerId ?? '').trim();
    if (!id) {
      return;
    }
    // Two links opened before either answers: only the latest request may land in the
    // drawer, or the slower earlier answer would replace the customer the attendant asked for.
    this.openCustomerRequestSeq += 1;
    const requestSeq = this.openCustomerRequestSeq;
    this.customerService.getCustomer(id).subscribe({
      next: response => {
        if (requestSeq !== this.openCustomerRequestSeq) {
          return;
        }
        const raw = response?.data?.customer ?? response?.data;
        if (!response?.status || !raw) {
          return;
        }
        this.selectedCustomer = this.transformCustomer(raw);
        this.showCustomerModal = true;
      },
      error: error => {
        console.error('Could not open customer from the prepaid redemption log:', error);
      },
    });
  }

  onCustomerStatusUpdated() {
    // Refresh the customer list to reflect the status change
    this.loadCustomers();
  }

  onCustomerUpdated() {
    // Refresh the customer list to reflect any customer data changes
    this.loadCustomers();
    this.refreshSelectedCustomer();
  }

  /**
   * CWS-1925: the open details modal holds the row captured at click time, so a save made from
   * inside it (a family member edit, say) would otherwise keep showing the old name and phone.
   * Fetched by id because the customer may not be on the reloaded page; only the identity
   * fields are taken, so the row keeps the list-only values the modal also reads.
   */
  private refreshSelectedCustomer() {
    const open = this.selectedCustomer;
    if (!open?.id) {
      return;
    }
    const requestId = ++this.openCustomerRequestId;
    this.customerService
      .getCustomer(open.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const fresh = response?.data ?? response;
          // Only the latest request counts, so a slower older reply cannot undo a newer one.
          if (
            !fresh ||
            requestId !== this.openCustomerRequestId ||
            this.selectedCustomer?.id !== open.id
          ) {
            return;
          }
          // A field the server cleared (null) is taken as cleared.
          const current = this.selectedCustomer;
          const pick = (key: 'first_name' | 'last_name' | 'contact' | 'email') =>
            fresh[key] === undefined ? current[key] : fresh[key];
          const firstName = pick('first_name');
          const lastName = pick('last_name');
          this.selectedCustomer = {
            ...current,
            first_name: firstName,
            last_name: lastName,
            contact: pick('contact'),
            email: pick('email'),
            name: this.getFullName(firstName, lastName),
            initials: this.getInitials(firstName, lastName),
          };
        },
        error: () => {
          // Keep what the modal shows; the list reload reports its own errors.
        },
      });
  }

  // Pagination data for the shared pagination bar (1-based page)
  get paginationData(): PaginationData {
    return {
      currentPage: this.pagination.page,
      totalPages: Math.max(1, Math.ceil(this.pagination.total / this.pagination.limit)),
      totalItems: this.pagination.total,
      itemsPerPage: this.pagination.limit,
    };
  }

  // Pagination event handlers
  onPageChange(page: number) {
    this.filters.page = page; // Already 1-based from the shared component
    this.loadCustomers();
  }

  onPageSizeChange(newSize: number) {
    this.filters.limit = newSize;
    this.filters.page = 1; // Reset to first page when changing page size
    this.loadCustomers();
  }

  onRowsPerPageChange(newSize: number) {
    this.onPageSizeChange(newSize);
  }

  // Helper methods for pagination
  get totalRecords(): number {
    return this.pagination.total;
  }

  get currentPage(): number {
    return this.pagination.page - 1; // Convert to 0-based for PrimeNG
  }

  get rowsPerPage(): number {
    return this.pagination.limit;
  }

  set rowsPerPage(value: number) {
    this.pagination.limit = value;
  }

  // Status Filter Methods
  isStatusSelected(value: string): boolean {
    return this.selectedStatuses.includes(value);
  }

  onStatusToggle(value: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedStatuses.includes(value)) {
        this.selectedStatuses = [...this.selectedStatuses, value];
      }
      return;
    }
    this.selectedStatuses = this.selectedStatuses.filter(v => v !== value);
  }

  applyStatusFilter() {
    // Update filters with selected statuses
    // If both statuses are selected (all options), don't apply filter
    if (
      this.selectedStatuses.length > 0 &&
      this.selectedStatuses.length < this.statusOptions.length
    ) {
      this.filters.customer_status = this.selectedStatuses.join(',');
    } else {
      delete this.filters.customer_status;
    }

    // Reset to first page when applying filters
    this.filters.page = 1;

    this.loadCustomers();
    this.isStatusFilterOpen = false;
    this.statusTrigger?.closeMenu();
  }

  clearStatusFilter() {
    this.selectedStatuses = [];
    delete this.filters.customer_status;
    this.filters.page = 1;
    this.loadCustomers();
    this.isStatusFilterOpen = false;
    this.statusTrigger?.closeMenu();
  }

  get hasStatusFilterValues(): boolean {
    return this.selectedStatuses.length > 0;
  }

  // Last Visit Filter Methods
  applyLastVisitFilter() {
    if (!this.canApplyLastVisitFilter) {
      return;
    }

    // Handle custom date range
    if (this.selectedLastVisitFilter === 'custom') {
      if (this.customStartDate && this.customEndDate) {
        this.filters.start_date = this.formatDateForAPI(this.customStartDate);
        this.filters.end_date = this.formatDateForAPI(this.customEndDate);
      }
    } else if (this.selectedLastVisitFilter) {
      // Handle predefined filters - calculate actual dates
      const dateRange = this.calculateDateRange(this.selectedLastVisitFilter);
      if (dateRange) {
        this.filters.start_date = this.formatDateForAPI(dateRange.startDate);
        this.filters.end_date = this.formatDateForAPI(dateRange.endDate);
      }
    } else {
      // Clear all last visit filters
      delete this.filters.start_date;
      delete this.filters.end_date;
    }

    // Reset to first page when applying filters
    this.filters.page = 1;

    this.loadCustomers();
    this.isLastVisitFilterOpen = false;
    this.lastVisitTrigger?.closeMenu();
  }

  clearLastVisitFilter() {
    this.selectedLastVisitFilter = '';
    this.customStartDate = null;
    this.customEndDate = null;
    this.lastVisitDateRangeError = null;
    delete this.filters.start_date;
    delete this.filters.end_date;
    this.filters.page = 1;
    this.loadCustomers();
    this.isLastVisitFilterOpen = false;
    this.lastVisitTrigger?.closeMenu();
  }

  onCustomStartDateChange(date: Date | null): void {
    this.customStartDate = date;
    if (date && this.customEndDate && this.toDayTime(date) > this.toDayTime(this.customEndDate)) {
      this.customEndDate = null;
    }
    this.validateLastVisitDateRange();
  }

  onCustomEndDateChange(date: Date | null): void {
    this.customEndDate = date;
    this.validateLastVisitDateRange();
  }

  get hasLastVisitFilterValues(): boolean {
    return !!this.selectedLastVisitFilter || !!(this.filters.start_date && this.filters.end_date);
  }

  get isCustomDateRange(): boolean {
    return this.selectedLastVisitFilter === 'custom';
  }

  get canApplyLastVisitFilter(): boolean {
    if (!this.selectedLastVisitFilter) {
      return false;
    }
    if (!this.isCustomDateRange) {
      return true;
    }
    return !!this.customStartDate && !!this.customEndDate && !this.lastVisitDateRangeError;
  }

  private validateLastVisitDateRange(): void {
    if (
      this.customStartDate &&
      this.customEndDate &&
      this.toDayTime(this.customStartDate) > this.toDayTime(this.customEndDate)
    ) {
      this.lastVisitDateRangeError = 'End date must be on or after the start date';
      return;
    }
    this.lastVisitDateRangeError = null;
  }

  private toDayTime(date: Date): number {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized.getTime();
  }

  private formatDateForAPI(date: Date): string {
    // Format as YYYY-MM-DD using local timezone to avoid off-by-one day issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Calculate a start and end Date for a given preset filter key (e.g. today, last_7_days).
   * @example
   * calculateDateRange('this_month')
   * { startDate: new Date(), endDate: new Date() }
   * @param {{string}} {{filterType}} - Filter key specifying the desired date range ('today', 'yesterday', 'this_week', 'last_7_days', 'this_month', 'last_month', 'this_year').
   * @returns {{ { startDate: Date; endDate: Date } | null }} Return object with startDate and endDate set to the bounds for the requested range, or null if the filterType is unsupported.
   **/
  private calculateDateRange(filterType: string): { startDate: Date; endDate: Date } | null {
    const today = new Date();
    const startDate = new Date();
    const endDate = new Date();

    switch (filterType) {
      case 'today':
        // Both dates set to today at 23:59:59
        startDate.setHours(23, 59, 59, 999);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'yesterday':
        // Both dates set to yesterday at 23:59:59
        startDate.setDate(today.getDate() - 1);
        startDate.setHours(23, 59, 59, 999);
        endDate.setDate(today.getDate() - 1);
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'this_week': {
        const dayOfWeek = today.getDay();
        const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        startDate.setDate(today.getDate() - daysFromMonday);
        startDate.setHours(0, 0, 0, 0);
        endDate.setDate(today.getDate() + (6 - daysFromMonday));
        endDate.setHours(23, 59, 59, 999);
        break;
      }

      case 'last_7_days':
        // Set to last 7 days: 7 days ago to today
        startDate.setDate(today.getDate() - 6); // 7 days ago (including today = 7 days total)
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999); // End of today
        break;

      case 'this_month':
        // Set to current month: 1st day to last day
        startDate.setFullYear(today.getFullYear(), today.getMonth(), 1); // 1st day of current month
        startDate.setHours(0, 0, 0, 0);

        // Last day of current month
        endDate.setFullYear(today.getFullYear(), today.getMonth() + 1, 0); // Setting day to 0 gets last day of current month
        endDate.setHours(23, 59, 59, 999);
        break;

      case 'last_month': {
        const prevMonth = today.getMonth() - 1;
        const prevYear = prevMonth < 0 ? today.getFullYear() - 1 : today.getFullYear();
        const adjustedMonth = prevMonth < 0 ? 11 : prevMonth;
        startDate.setFullYear(prevYear, adjustedMonth, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setFullYear(prevYear, adjustedMonth + 1, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      }

      case 'this_year':
        // Set to current year: January 1st to December 31st
        startDate.setFullYear(today.getFullYear(), 0, 1); // January 1st of current year
        startDate.setHours(0, 0, 0, 0);
        endDate.setFullYear(today.getFullYear(), 11, 31); // December 31st of current year
        endDate.setHours(23, 59, 59, 999);
        break;

      default:
        return null;
    }

    return { startDate, endDate };
  }

  // Search Methods
  onSearchInput(event: any) {
    const searchTerm = (event?.target?.value ?? this.searchText ?? '').trim();
    this.searchText = searchTerm;
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.applySearch(searchTerm);
    }, 300);
  }

  applySearch(searchTerm: string) {
    if (searchTerm) {
      this.filters.search = searchTerm;
    } else {
      delete this.filters.search;
    }

    // Reset to first page when searching
    this.filters.page = 1;
    this.loadCustomers();
  }

  clearSearch() {
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
    this.searchText = '';
    delete this.filters.search;
    this.filters.page = 1;
    this.loadCustomers();
  }

  /**
   * Clears search text and all table filters (status, last visit), then reloads customers.
   * Used by the empty-state CTA when filtered results return no rows.
   */
  clearAllFilters() {
    this.searchText = '';
    this.selectedStatuses = [];
    this.selectedLastVisitFilter = '';
    this.customStartDate = null;
    this.customEndDate = null;
    this.lastVisitDateRangeError = null;
    this.appliedDiscountPageFilters = createDefaultCustomerDiscountPageFilters();

    delete this.filters.search;
    delete this.filters.customer_status;
    delete this.filters.start_date;
    delete this.filters.end_date;
    delete this.filters.discount_applied;
    delete this.filters.id;

    this.filters.page = 1;
    this.loadCustomers();
  }

  onAddCustomerClick() {
    this.addCustomerClick.emit();
  }

  // Custom sort function for PrimeNG table
  /**
   * Sorts the provided data array in-place based on the given sort field and order and updates the component's current sort field and order.
   * @example
   * customSort({ field: 'name', order: 1, data: [{ name: 'Bob' }, { name: 'Alice' }] })
   * undefined
   * @param {{any}} {{event}} - Sorting event object containing `field` (string), `order` (1 for asc, -1 for desc, or other), and `data` (array to be sorted).
   * @returns {{void}} Does not return a value; the data array is sorted in-place and component state is updated.
   **/
  customSort(event: any) {
    // Update our tracking variables
    this.currentSortField = event.field || '';
    this.currentSortOrder = event.order === 1 ? 'asc' : event.order === -1 ? 'desc' : '';

    // Perform the sorting
    event.data.sort((data1: Customer, data2: Customer) => {
      const value1 = this.getFieldValue(data1, event.field);
      const value2 = this.getFieldValue(data2, event.field);

      let result = null;

      if (value1 == null && value2 != null) {
        result = -1;
      } else if (value1 != null && value2 == null) {
        result = 1;
      } else if (value1 == null && value2 == null) {
        result = 0;
      } else if (event.field === 'name') {
        // Handle string sorting for name field
        const str1 = String(value1 || '').toLowerCase();
        const str2 = String(value2 || '').toLowerCase();
        result = str1.localeCompare(str2);
      } else {
        // Handle other types of sorting
        result = value1 < value2 ? -1 : value1 > value2 ? 1 : 0;
      }

      return event.order * result;
    });
  }

  // Helper method to get field value from object
  /**
   * Returns the value for a given field from a Customer object, handling specific keys and dot-notation for nested properties.
   * @example
   * getFieldValue(customer, 'name')
   * 'Acme Corp'
   * @param {{Customer}} {{obj}} - Customer object to read the field value from.
   * @param {{string}} {{field}} - Field name to retrieve; supports specific keys ('name', 'lastVisit', 'customer_status') and dot notation for nested properties.
   * @returns {{any}} Value of the requested field (string, Date, or nested property value).
   **/
  private getFieldValue(obj: Customer, field: string): any {
    switch (field) {
      case 'name':
        return obj.name || '';
      case 'vehicles':
        return obj.vehicles || '';
      case 'license':
        return obj.licensePlates || obj.license_plate || '';
      case 'visits':
        return obj.numberOfVisits ?? 0;
      case 'revenue': {
        const raw = String(obj.revenue ?? '').replace(/[^0-9.-]/g, '');
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      }
      case 'totalDiscountRedeemed': {
        const raw = String(obj.totalDiscountRedeemed ?? '').replace(/[^0-9.-]/g, '');
        const n = Number(raw);
        return Number.isFinite(n) ? n : 0;
      }
      case 'lastVisit':
        return new Date(obj.updated_at || obj.created_at);
      case 'status':
      case 'customer_status':
        return obj.customer_status || '';
      default:
        return field.split('.').reduce((o: any, f) => o && o[f], obj);
    }
  }

  /**
   * Returns the appropriate icon path for the given column sort field.
   * @example
   * etSortIcon('name')
   * 'assets/images/customer/tabler_sort-ascending-letters.svg'
   * @param {{string}} {{field}} - The name of the column/field to determine the sort icon for.
   * @returns {{string}} The relative path to the icon asset representing the sort state for the provided field.
   **/
  getSortIcon(field: string): string {
    if (this.currentSortField !== field || !this.currentSortOrder) {
      return 'assets/images/package/tabler_arrows-sort.svg';
    }

    // Use letter-based icons for name field
    if (field === 'name') {
      if (this.currentSortOrder === 'asc') {
        return 'assets/images/customer/tabler_sort-ascending-letters.svg';
      }
      return 'assets/images/customer/sort-descending-letters.svg';
    }
    return 'assets/images/package/tabler_arrows-sort.svg';
  }

  trackBySkeletonId(_: number, row: { id: number }): number {
    return row.id;
  }
  trackByValue(_: number, option: { value: string }): string {
    return option.value;
  }
}
